import React, { useEffect, useState } from 'react';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { accessService } from '../../../services/central-api/access';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import InternetCompanyModal from './InternetCompanyModal';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import CustomButton from '../../../components/CustomButton';
import MyDataTable from '../../../components/MyDataTable';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

export default function InternetCompany({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'InternetCompanies'>>();
    const { user: authenticatedUser } = useAuth();

    const [internetCompanies, setInternetCompanies] = useState<InternetCompanyList[]>([]);
    const [selectedInternetCompany, setSelectedInternetCompany] = useState<InternetCompany>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessInternetCompanies', filters],
        queryFn: () => accessService.getInternetCompanies(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setInternetCompanies(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(internetCompany: InternetCompany) {
        try {
            if (internetCompany?.id) {
                await accessService.deleteInternetCompany(internetCompany.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();

            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('internetCompanies')}
                actions={<CustomButton
                    onPress={() => {
                        setSelectedInternetCompany(undefined);
                        setModalVisible(true);
                    }}
                    text={translate('register')}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredInternetCompanies')}
                    zIndex={2}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[{
                            name: translate('name'),
                            selector: row => row.name,
                            grow: 2,
                            wrap: true
                        },
                        {
                            name: translate('cameraCount'),
                            selector: row => row.cameraCount,
                            grow: 2,
                        },
                        {
                            button: true,
                            cell: row => <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedInternetCompany(row);
                                        setModalVisible(true);
                                    }
                                }, {
                                    content: translate('delete'),
                                    onPress: () => {
                                        setSelectedInternetCompany(row);
                                        setDeleteModalVisible(true);
                                    },
                                    variant: 'danger',
                                }]}

                            />
                        }]}
                        data={internetCompanies}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            {/* Delete Modal */}
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={String(selectedInternetCompany?.name) || ''}
                onSubmit={async () => {
                    if (selectedInternetCompany) {
                        await confirmDelete(selectedInternetCompany);
                    }
                }}
            />

            {/* Create/Edit Modal */}
            <InternetCompanyModal
                onClose={() => {
                    setSelectedInternetCompany(undefined);
                    setModalVisible(false);
                    refetch();
                }}
                isModalVisible={isModalVisible}
                internetCompanyId={selectedInternetCompany?.id}
            />
        </Container>
    );
}
